import { type ProjectCode } from '@constants'
import { Box } from '@pets-ui/Box/Box'
import { ButtonBase } from '@pets-ui/ButtonBase/ButtonBase'
import { Container } from '@pets-ui/Container/Container'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowDownSmall } from '@pets-ui/icons'
import { Paper, type PaperProps } from '@pets-ui/Paper/Paper'
import { Tab } from '@pets-ui/Tab/Tab'
import { TabList } from '@pets-ui/TabList/TabList'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { zIndex } from '@pets-ui/tokens/zIndex.stylex'
import * as stylex from '@stylexjs/stylex'
import { useState, type ReactNode } from 'react'
import { CategoryHighlight } from './CategoryHighlight'
import { CategoryTabsDropdown } from './CategoryTabsDropdown'

const styles = stylex.create({
  root: {
    position: 'sticky',
    zIndex: zIndex.fab,
    top: 56,
  },
  wrapper: {
    display: 'flex',
    position: 'relative',
    maxWidth: '100vw',
  },
  categories: {
    'scrollbarWidth': 'none', // firefox scroll bar
    'overflowX': 'auto',
    '::-webkit-scrollbar': {
      display: 'none', // webkit scroll bar
    },
  },
  toggleBtn: {
    padding: '12px 10px',
    backgroundColor: colors.white,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  iconActive: {
    transform: 'rotate(180deg)',
  },
})

export interface Category {
  label: string
  value: string
  highlight?: boolean
  icon?: ReactNode
}

export interface CategoryTabsProps extends Omit<PaperProps, 'onChange'> {
  projectCode: ProjectCode
  categories: Category[]
  activeCategory: string
  isExpand: boolean
  onChange: (category: Category) => void
  onToggle: () => void
}

export const CategoryTabs = ({
  projectCode,
  categories,
  activeCategory,
  isExpand,
  onChange,
  onToggle,
  rootStyle,
  ...rest
}: CategoryTabsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

  return (
    <Paper borderRadius={0} rootStyle={[styles.root, rootStyle]} {...rest}>
      <Container>
        <Box rootRef={setAnchorEl} width="100%" />
        <div {...stylex.props(styles.wrapper)}>
          <TabList rootStyle={styles.categories} value={activeCategory}>
            {categories.map(category => (
              <Tab
                key={category.value}
                id={projectCode + '-' + category.value}
                onClick={() => onChange(category)}
                value={category.value}
              >
                <HStack gap={2} position="relative">
                  {category.icon}
                  <Text variant="subtitle">{category.label}</Text>
                  {category.highlight && <CategoryHighlight right={-5} />}
                </HStack>
              </Tab>
            ))}
          </TabList>
          <ButtonBase
            rootStyle={styles.toggleBtn}
            onClick={onToggle}
          >
            <ArrowDownSmall fontSize={20} color={colors.grey500} rootStyle={isExpand && styles.iconActive} />
          </ButtonBase>
        </div>
      </Container>
      <CategoryTabsDropdown
        anchorEl={anchorEl}
        categories={categories}
        activeCategory={activeCategory}
        isExpand={isExpand}
        onChange={onChange}
        onToggle={onToggle}
      />
    </Paper>
  )
}
